import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { App } from "@/app/App";
import { preloadGuideBySlug } from "@/features/guides/registry";
import { preloadNewsById } from "@/features/news/registry";
import { ensureLocaleTranslations } from "@/lib/data";
import { extractLocaleFromPath } from "@/lib/i18n/localize-path";
import "@/styles/globals.css";

// hydrateRoot (not createRoot): item/NPC/location/guide/news/trust routes now ship pre-rendered
// body HTML (see scripts/generate-static-body.ts) — hydrating attaches to that markup instead of
// discarding and re-rendering it from scratch. Routes with no prerendered body (category/index
// pages, tracker, map, etc.) still work fine: React hydrates against an effectively-empty
// #root and falls back to a client render for that subtree, same as before.
//
// Item/location names for the page's own locale must be in memory before that hydrateRoot call,
// not after: lib/data.ts loads translations per-locale on demand now (see its doc comment), and
// the prerendered HTML already has the real names baked in. Hydrating before the fetch resolves
// would make the first client render show English/fallback text where the server HTML shows the
// translated one — a React #418 mismatch, the same class of bug usePersistentState's doc comment
// describes for localStorage-backed state. The prerendered markup is already fully painted, so
// this delay is invisible — it just postpones when hydration (interactivity) attaches, not when
// the page becomes visible.
//
// A guide/news detail page is the same trap for a different piece of data: its body (blocks[])
// is now a lazy per-file import too (see features/guides/registry.ts), and the prerendered HTML
// already has that body's markup. If the path is a guide or news detail route, preload that one
// article before hydrating — every other route's preload call below is just a no-op promise.
const { locale, path } = extractLocaleFromPath(window.location.pathname);
const guideSlug = path.match(/^\/guides\/([^/]+)\/?$/)?.[1];
const newsId = path.match(/^\/news\/([^/]+)\/?$/)?.[1];

Promise.all([
  ensureLocaleTranslations(locale),
  guideSlug ? preloadGuideBySlug(locale, guideSlug) : Promise.resolve(),
  newsId ? preloadNewsById(locale, newsId) : Promise.resolve(),
]).then(() => {
  hydrateRoot(
    document.getElementById("root")!,
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
